/**
 * DECISIONS 200 step 5 (the owner's question: "how do you know if those troops are available to drop?  Would they be a new
 * ephemeral squad?  Or join respawn waves after they die?" — the coordinator's answer, his word: "Ok that dropship respawn flow
 * lean is pretty good"): the units a pod drop or a called dropship brings arrive as one squad of their own, with the caller's
 * order — hold at the drop point — and each member's squad before it remembered. As one goes down it goes back to that squad,
 * into the waves' pool like any fallen unit; the drop squad dissolves when none is left (or 68.10's fluid squads take its members
 * on). Pure; with no squads in the match nothing happens.
 */
import type { GameState, MatchSquad, SquadOrder, Vec3 } from '../types';
import { dropshipAboard } from './dropship';

/** Unit `unitId` (just come in) joins the drop squad for `key` (a pod drop, a dropship): made with its first, by the caller's order — `order` (69.3b step 3: the bot commander's, its objective), else holding at `at`. */
export function joinDropSquad(state: GameState, key: string, team: number, unitId: number, at: { planetIndex: number; surfaceDir: Vec3 }, by?: 'human' | 'commander', order?: SquadOrder): GameState {
  if (!state.squads) return state;
  const from = state.squads.find(s => s.unitIds.includes(unitId));
  if (from?.drop?.key === key) return state;
  let squads = state.squads.map(s => (s.unitIds.includes(unitId) ? { ...s, unitIds: s.unitIds.filter(x => x !== unitId) } : s));
  const mine = squads.find(s => s.drop?.key === key);
  const was = from ? (from.drop ? from.drop.from[unitId] ?? -1 : from.id) : -1;   // (from another drop squad: where it came from first)
  if (mine) {
    squads = squads.map(s => (s === mine ? { ...s, unitIds: [...s.unitIds, unitId], drop: { key, from: { ...s.drop!.from, [unitId]: was } } } : s));
  } else {
    const id = state.squads.reduce((m, s) => Math.max(m, s.id), -1) + 1;
    const squad: MatchSquad = { id, team, unitIds: [unitId], base: order ?? { kind: 'hold', planetIndex: at.planetIndex, surfaceDir: at.surfaceDir }, tasks: [], ...(by ? { baseBy: by } : {}), ...(state.config.squadLead ? { leader: unitId } : {}), drop: { key, from: { [unitId]: was } } };
    squads = [...squads, squad];
  }
  return { ...state, squads };
}

/**
 * The drop squads' members that are down (and not aboard a ship or waiting for one) go back to the squad each came from (or,
 * that one gone, its side's first); a drop squad left with nobody is dissolved. Run before a wave, so they come back as any
 * fallen unit does. The state unchanged when nothing moved.
 */
export function stepDropSquads(state: GameState): GameState {
  if (!state.squads?.some(s => s.drop)) return state;
  const aboard = dropshipAboard(state);
  let squads = state.squads, changed = false;
  for (const ds of state.squads.filter(s => s.drop)) {
    const down = ds.unitIds.filter(id => !state.players.find(p => p.id === id)?.alive && !aboard.has(id));
    if (!down.length && ds.unitIds.length) continue;
    changed = true;
    squads = squads.map(s => (s.id === ds.id ? { ...s, unitIds: s.unitIds.filter(id => !down.includes(id)) } : s));
    for (const id of down) {
      const home = squads.find(s => s.id === ds.drop!.from[id] && !s.drop) ?? squads.filter(s => s.team === ds.team && !s.drop).sort((a, b) => a.id - b.id)[0];
      if (home) squads = squads.map(s => (s.id === home.id ? { ...s, unitIds: [...s.unitIds, id] } : s));
    }
    if (!squads.find(s => s.id === ds.id)!.unitIds.length) squads = squads.filter(s => s.id !== ds.id);
  }
  return changed ? { ...state, squads } : state;
}
